import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useSmarticoUserInfos } from '~/stores/smartico/userInfos';
import { extractVipTags, vipTierFromTags } from '~/utils/vip';

/**
 * Status VIP do usuário logado, lido das `core_public_tags` do perfil da
 * Smartico (store `smarticoUserInfos`).
 *
 * De onde vem o dado: o store guarda o retorno cru de
 * `_smartico.api.getUserProfile()` em `userInfosProfile`, e os listeners
 * `identify` / `props_change` (stores/smartico/init.ts) re-mergeiam o perfil —
 * então mudança de tag no CRM reflete aqui sem reload.
 *
 * O que NÃO fazer: tratar `isVip === false` como "não é VIP". O store não
 * persiste (shared/storeConfig.ts é vazio), logo em todo boot existe uma janela
 * de alguns instantes em que o perfil ainda não chegou e TODO mundo parece não
 * ser VIP. Quem precisa distinguir "não é" de "ainda não sei" usa `isVipReady`.
 * Pra só ENFEITAR (chip, selo), `isVip` basta: aparece quando o dado chega.
 */
export function useVipStatus() {
  const { userInfosProfile } = storeToRefs(useSmarticoUserInfos());

  // O perfil chegou (mesmo sem nenhuma tag) — é o sinal de "já sei a resposta".
  const isVipReady = computed(() =>
    Array.isArray(userInfosProfile.value?.core_public_tags),
  );

  const vipTags = computed(() =>
    extractVipTags(userInfosProfile.value?.core_public_tags),
  );

  const isVip = computed(() => vipTags.value.length > 0);

  // Nível numerado (`VIP_3` → 3) ou `null` quando o usuário é VIP sem tier
  // (`VIP_SIGNATURE`) ou não é VIP. É o que escolhe a versão do card: quem não tem
  // número fica com a arte padrão.
  // CUIDADO: `VIP_0` existe e vira `0` — que é falsy. Testar sempre
  // `vipTier !== null`, nunca `if (vipTier)`, senão o tier 0 é tratado como
  // "sem tier" e perde a arte dele.
  const vipTier = computed(() =>
    vipTierFromTags(userInfosProfile.value?.core_public_tags),
  );

  return { isVip, isVipReady, vipTags, vipTier };
}
